import { environment } from '../../../environments/environment';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, switchMap, throwError } from 'rxjs';
import { catchError, mergeMap, retry, tap } from 'rxjs/operators';
import { AlertifyService, SessionCheckService } from 'ccc-commons/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private alert: AlertifyService,
    private router: Router,
    private sessionCheckService: SessionCheckService,
  ) { }


  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.handleRequest(req, next))
      .pipe(
        tap(
          (event: HttpEvent<any>) => { },
          (err: any) => {
            // Handle the global errors here
            if (err instanceof HttpErrorResponse && err.status === 401) {
              this.oktaAuth.signInWithRedirect();
              this.sessionCheckService.resetSessionState();
            } else if (
              err instanceof HttpErrorResponse &&
              err.status === 480) {
              this.router.navigate(['account-error'], {
                state: {
                  title: 'Your account has not been configured properly.',
                  subject: `Organization Admin Account Error: User not in ${environment.title} Master DB`
                }
              });
            } else if (
              err instanceof HttpErrorResponse &&
              err.status === 481) {
              this.router.navigate(['account-error'], {
                state: {
                  title: 'Your account is inactive.',
                  message: 'Please contact an Administrator'
                }
              });
            } else if (
              err instanceof HttpErrorResponse &&
              err.status !== 400 &&
              err.status !== 409 &&
              err.status !== 422
            ) {
              if (err.status === 500 &&
                (err.url.includes('licenses/import') || err.url.includes('accounts/import'))) return;
              this.alert.httpError(err);
            }
          }
        )
      );
  }

  /**
   * Handles the request to set the headers, waiting for the access token
   * @param request
   * @param next
   */
  private handleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.oktaAuth.getAccessToken();
    let requestClone;

    let headers: { [key: string]: string };
    const acceptHeaderValue =
      'application/json;application/xml;application/pdf;multipart/form-data;application/x-www-form-urlencoded';
    const authHeaderValue = `Bearer ${accessToken}`;
    accessToken ? headers = { Accept: acceptHeaderValue, Authorization: authHeaderValue } : headers = { Accept: acceptHeaderValue };

    requestClone = request.clone({
      setHeaders: headers
    });

    if (this.shouldSkipSessionCheck(requestClone)) {
      return next.handle(requestClone);
    }

    if (this.sessionCheckService.isSessionKnownExpired()) {
      return throwError(() => new HttpErrorResponse({ error: null, status: 401, statusText: 'Session expired' }));
    }

    // Check if session is valid, handle session expired and
    // handle errors during validation
    return from(this.sessionCheckService.isSessionValid()).pipe(
      switchMap(isValid => {
        console.log('Session is not valid, redirecting to login');
        if (!isValid) {
          return this.handleAuthError('Session expired, redirecting to login', 401);
        }
        // Session is valid, proceed with the request
        return next.handle(requestClone).pipe(
          catchError((httpError) => {
            // Handle HTTP errors from the actual request
            if (httpError.status === 401) {
              // Additional check for 401 responses
              return this.handleAuthError('Unauthorized request, redirecting to login', 401);
            }
            return throwError(() => new HttpErrorResponse(httpError));
          })
        );
      }),
      catchError((error) => {
        // Handle session validation errors
        this.sessionCheckService.resetSessionState();
        return throwError(() => new HttpErrorResponse(error));
      })
    );
  }

  /**
   * handleAuthError
   * handles the authentication error by redirecting to the login page
   * @param errorMessage string
   * @returns Observable<never>
   */
  private handleAuthError(errorMessage: string, errorCode: number): Observable<never> {
    this.router.navigate(['/login']);
    this.sessionCheckService.resetSessionState();
    return throwError(() => new HttpErrorResponse({ error: null, status: errorCode, statusText: errorMessage }));
  }

  /**
   * isAuthError
   * returns true if the error response is an authentication error
   * @param error the http error response
   */
  private isAuthError(error: HttpErrorResponse): boolean {
    return error.status === 401 || error.status === 403;
  }

  /**
   * shouldSkipSessionCheck
   * if the request goes to the authentication APIs, skip check
   * @param req the HTTP request
   */
  private shouldSkipSessionCheck(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();

    // Skip for login, logout, and Okta API endpoints
    return url.includes('/login/') ||
      url.includes('/logout') ||
      url.includes('/oauth2') ||
      url.includes('/api/v1/sessions') ||
      url.includes('okta.com');
  }
}
